// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/ui-core/page-transition.tsx
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/fleet/ui/src/page-transition.tsx
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo cash-sync-app
//     node fleet/fleet.ts check --repo cash-sync-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
import { Transition } from "./transition";
import { type JSX, Show } from "solid-js";

type PageTransitionVariant = "slide" | "fade" | "zoom" | "depth" | "ios" | "android" | "native";

interface PageTransitionProps {
  routeKey: string;
  children: JSX.Element;
  class?: string;
  variant?: PageTransitionVariant;
}

/**
 * Safety cap for the end of a page transition.
 *
 * `solid-transition-group@0.3` does NOT measure the declared duration: it adds
 * the classes and then waits for a `transitionend`/`animationend`. If that event
 * never arrives it NEVER calls `done()`, and with `mode="outin"` that means the
 * OUTGOING element is not unmounted and the INCOMING one is not mounted: the URL
 * changes and the previous screen stays stuck forever.
 *
 * This is not a hypothesis: it is exactly what happened when `global.css` lost
 * the `.page-android-*` rules (see the restoration commit). Measured: after
 * pressing "← Back to home" the URL moved to `/u/0` and the DOM was still the
 * login, with the node frozen in
 * `page-android-exit-active page-android-exit-to`.
 *
 * The CSS is restored, but a failure like that must NOT be able to leave the app
 * unusable in silence again: the watchdog degrades the cut to "no animation"
 * instead of blocking navigation. Generous against the real 120–220 ms of the
 * rules, so a legitimate animation is never cut short.
 */
const TRANSITION_FALLBACK_MS = 600;

/**
 * Waits for the real end of `el`'s transition, with a time cap.
 *
 * Passing `onEnter`/`onExit` with arity 2 makes the library stop registering its
 * own listeners and delegate closing to us (see `enterTransition` /
 * `exitTransition` in solid-transition-group): that is why this has to listen
 * for both events AND call `done()` no matter what.
 */
function settleWhenDone(el: Element, done: () => void): void {
  let settled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const finish = (): void => {
    if (settled) {
      return;
    }
    settled = true;
    clearTimeout(timer);
    el.removeEventListener("transitionend", handleEnd);
    el.removeEventListener("animationend", handleEnd);
    done();
  };

  // The element itself only: a `transitionend` bubbled up from a child (a button
  // with `transition-colors`, say) would close the transition early and the cut
  // would be seen half-done.
  function handleEnd(event: Event): void {
    if (event.target === el) {
      finish();
    }
  }

  el.addEventListener("transitionend", handleEnd);
  el.addEventListener("animationend", handleEnd);
  timer = setTimeout(finish, TRANSITION_FALLBACK_MS);
}

export function PageTransition(props: PageTransitionProps) {
  const transitionName = () => {
    const v = props.variant ?? "native";
    if (v === "native") {
      // Detect if user is on iOS or macOS touch device
      const isIOS =
        /iPad|iPhone|iPod/u.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
      return isIOS ? "page-ios" : "page-android";
    }
    return `page-${v}`;
  };

  return (
    <Transition
      name={transitionName()}
      // The CSS of these transitions names the initial enter state
      // `-enter-from` (Vue 3 convention), but solid-transition-group expects
      // `-enter`. Without this mapping the class that applies the initial state
      // does not exist, NO property changes on enter, the `transitionend` never
      // arrives and the `-enter-active`/`-enter-to` classes stay on forever —
      // with their `will-change: transform, opacity`, that is one live
      // compositing layer per navigation. Measured in the probe: the node
      // survived with `page-android-enter-active page-android-enter-to`.
      enterClass={`${transitionName()}-enter-from`}
      mode="outin"
      onEnter={settleWhenDone}
      onExit={settleWhenDone}
    >
      <Show when={props.routeKey} keyed>
        <div class={props.class}>{props.children}</div>
      </Show>
    </Transition>
  );
}
